import { Close, Check } from "@mui/icons-material";
import { Stack, Typography, IconButton, AlertColor } from "@mui/material";
import format from "date-fns/format";
import { useState, useMemo, useCallback } from "react";
import { useUpdateCurrentPeriodEndMutation } from "src/api/services/subscriptionsApi";
import { PatientSubscriptions } from "src/api/types";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { isError } from "src/utils/isError";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import DatePicker from "src/components/DatePicker";
import TimedAlert from "src/shared/TimedAlert";

interface Props {
  subscription: PatientSubscriptions;
}

const SubscriptionText = ({ subscription }: Props) => {
  const errorLog = useSentryLogger("error");
  const [updateSubscription, { isLoading }] =
    useUpdateCurrentPeriodEndMutation();
  const clinicianName = useClinician((c) => `${c.first_name} ${c.last_name}`);
  const [openCalendar, setOpenCalendar] = useState<boolean>(false);
  const [date, setDate] = useState<Date | null>(
    new Date(subscription.current_period_end || ""),
  );
  const [alert, setAlert] = useState<{
    text: string;
    severity: AlertColor;
  } | null>(null);

  const canceled = useMemo(() => {
    const date = subscription.canceled_at
      ? new Date(subscription.canceled_at)
      : null;

    return subscription.status === "canceled" && date && !isNaN(date.getTime())
      ? format(date, "MMMM dd, yyyy")
      : null;
  }, [subscription.canceled_at, subscription.status]);

  const renewsOn = useMemo(() => {
    const date = subscription.current_period_end
      ? new Date(subscription.current_period_end)
      : null;

    return subscription.status === "paid" && date && !isNaN(date.getTime())
      ? format(date, "MMMM dd, yyyy")
      : null;
  }, [subscription.current_period_end, subscription.status]);

  const schedulesForCancellation = useMemo(() => {
    const date = subscription.scheduled_for_cancellation_at
      ? new Date(subscription.scheduled_for_cancellation_at)
      : null;

    return subscription.status === "scheduled_for_cancellation" &&
      date &&
      !isNaN(date.getTime())
      ? format(date, "MMMM dd, yyyy")
      : null;
  }, [subscription.scheduled_for_cancellation_at, subscription.status]);

  const handleCalendar = useCallback(() => {
    setOpenCalendar((open) => !open);
  }, []);

  const handleUpdateSubscription = useCallback(async () => {
    try {
      await updateSubscription({
        current_period_end: date?.toISOString(),
        id: subscription.id,
        clinician: clinicianName,
      }).unwrap();

      setAlert({
        text: `${subscription.display_name} refill date was successfully updated`,
        severity: "success",
      });
      setOpenCalendar(false);
    } catch (err: unknown) {
      if (isError(err)) {
        setAlert({
          text: err.data.message,
          severity: "error",
        });
        errorLog(err.data.message);
      } else {
        setAlert({ text: `Unable to update subscription`, severity: "error" });
        errorLog(`Unable to update subscription`);
      }
    }
  }, [
    clinicianName,
    date,
    errorLog,
    subscription.display_name,
    subscription.id,
    updateSubscription,
  ]);

  return (
    <Stack gap={0.5}>
      {canceled ? <Typography>{`Canceled on ${canceled}`}</Typography> : null}
      {renewsOn ? (
        <Stack spacing={0.5} direction="row" alignItems="center">
          <Typography>{`Renews on: ${renewsOn}`}</Typography>
          <IconButton size="small" onClick={handleCalendar}>
            {openCalendar ? <Close /> : <EditCalendarIcon />}
          </IconButton>
        </Stack>
      ) : null}
      {openCalendar ? (
        <Stack direction={"row"} spacing={0.5}>
          <DatePicker
            slots={{ openPickerIcon: EditCalendarIcon }}
            minDate={new Date()}
            fullWidth={false}
            onChange={setDate}
            value={date}
          />

          <IconButton
            size="small"
            onClick={handleUpdateSubscription}
            disabled={isLoading}
          >
            <Check />
          </IconButton>
        </Stack>
      ) : null}
      {schedulesForCancellation ? (
        <Typography>{`Scheduled for cancellation on: ${schedulesForCancellation}`}</Typography>
      ) : null}
      {alert != null ? <TimedAlert alert={alert} setAlert={setAlert} /> : null}
    </Stack>
  );
};

export default SubscriptionText;
